import * as React from "react";
import { useRef, useState } from "react";
import Page from "./commons/components/pagination/Page";
import Column, { Enum } from "./commons/components/pagination/Column";
import Sort from "./commons/components/pagination/Sort";
import PatientReportStatus from "./commons/model/report/PatientReportStatus";
import { StatusRejectionCause } from "./reports/Report";
import "./PatientList.scss";
import PatientInfo, {
  PatientInformation,
  PatientLinks,
  Tag,
} from "./commons/model/patient/PatientInfo";
import PaginatedList from "./PaginatedList";
import PatientTagSetter, { TagModalInfo } from "./patient-profile/PatientTagSetter";
import sfetch from "./commons/utils/super-fetch";
import ReportStatusUtils from "./reports/ReportStatusUtils";
import ReportTypeUtils from "./reports/ReportTypeUtils";
import InsuranceInfoStatus from "./commons/model/InsuranceInfoStatus";
import InsuranceInfoStatusUtils from "./patient-management/InsuranceInfoStatusUtils";
import FilterQuery, { Operator } from "./commons/components/pagination/queries/FilterQuery";
import HeaderBar from "./commons/components/HeaderBar";
import TagModal from "./settings/tags/TagModal";
import { Danger, Success } from "./commons/components/Alerts";
import { ModalType, TagModalStates } from "./settings/tags/TagModalTypeAndState";
import FlowType from "./commons/model/FlowType";
import AssignedToCell from "./AssignedToCell";
import { ModalWarning } from "./commons/components/modal/ModalWarning";
import EntitySettingsId from "./commons/model/EntitySettingsId";
import CheckCircledFull from "../resources/images/icons/check_circled_full.png";
import ReportComplexity from "./commons/model/report/ReportComplexity";
import ReportComplexityUtils from "./reports/ReportComplexityUtils";
import { SharingReportInfo } from "./reports/ReportModels";
import ShareReportModal from "./reports/ShareReportModal";
import { createRoot } from "react-dom/client";

interface PatientListProps {
  page: Page<PatientWithReportInfo>;
  columns: Column[];
  fetchUrl: string;
  sort: Sort;
  exportUrl: string;
  exportAuthorized: boolean;
  exportLimit: number;
  archived: boolean;
  availableTags: Tag[];
  createTagUrl: string;
  addTagToPatientUrl: string;
}

interface LatestReport {
  status: PatientReportStatus;
  type?: FlowType;
  clinicalScore?: number;
  complexity?: ReportComplexity;
  rejectionCause?: StatusRejectionCause;
  technicalErrorMsg?: string;
}

export interface PatientWithReportInfo extends PatientInfo {
  links: PatientLinks;
  latestReport: LatestReport;
}

export default function PatientList({
  page,
  columns,
  fetchUrl,
  sort,
  exportUrl,
  exportAuthorized,
  exportLimit,
  archived,
  availableTags,
  createTagUrl,
  addTagToPatientUrl,
}: PatientListProps) {
  const listRef = useRef<PaginatedList>();
  const [currentFilter, setCurrentFilter] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<{
    display: boolean;
    content?: string;
  }>({ display: false });
  const [addTagToPatientSuccessMessage, setAddTagToPatientSuccessMessage] =
    useState<boolean>(false);
  const [tagSelectedPatient, setTagSelectedPatient] = useState<PatientInformation>();
  const [displayedTags, setDisplayedTags] = useState<Tag[]>(availableTags);
  const [displayAddLabelModal, setDisplayAddLabelModal] = useState<boolean>(false);
  const [filtersQuery, setFiltersQuery] = useState<string>("");
  const [pageElementsCount, setPageElementsCount] = useState<number>(page.totalElements);
  const [showExportWarning, setShowExportWarning] = useState(false);
  const [showShareReportModal, setShowShareReportModal] = useState(false);
  const [sharingReportInfo, setSharingReportInfo] = useState<SharingReportInfo>(null);

  const handleTooltipClick = (evt) => evt.stopPropagation();

  const selectEmail = (email) => navigator.clipboard.writeText(email);

  const redirectTo = (url: string) => (document.location.href = url);

  const renderEmail = (patient: PatientWithReportInfo) => {
    const patientEmail = patient.patientInformation.email;
    return (
      <>
        <a
          onClick={(e) => {
            e.stopPropagation();
            redirectTo(patient.links.patientPageEmailLink);
          }}
          className="link"
        >
          {truncateIfMoreThanXChar(patientEmail, 30)}
        </a>
        {patientEmail && (
          <span className="tooltip" onClick={handleTooltipClick}>
            <i
              className="ic_copy"
              title={i18n["Global.Copy"]}
              onClick={() => selectEmail(patientEmail)}
            />
            {patientEmail}
          </span>
        )}
      </>
    );
  };

  const patientNameColumn = columns.find((it) => it.attribute === "PATIENT_NAME");
  patientNameColumn.renderer = (patient: PatientWithReportInfo) => {
    const patientName = patient.patientInformation.fullName;
    let isConvertedLead = patient.patientInformation.isConvertedLead;

    if (isConvertedLead) {
      return (
        <>
          <div className="v-flex">
            <div>
              <span>{truncateIfMoreThanXChar(patientName, 24)}</span>
              <img
                className="check-circled-full"
                src={CheckCircledFull}
                alt="check circled full"
              />{" "}
              &nbsp;
            </div>
            {renderEmail(patient)}
          </div>
        </>
      );
    } else
      return (
        <>
          <div className="v-flex">
            <span>{truncateIfMoreThanXChar(patientName, 24)}</span>
            {renderEmail(patient)}
          </div>
        </>
      );
  };

  const truncateIfMoreThanXChar = (stringToCut: string, maxChar: number) => {
    return stringToCut.length <= maxChar ? stringToCut : stringToCut.substring(0, maxChar) + "...";
  };

  const fillI18nColumnValues = (statusEnum: any, i18nTranslate: (any) => string) => {
    return Object.keys(statusEnum).reduce((previousValue, currentValue) => {
      previousValue[currentValue] = i18n[i18nTranslate(statusEnum[currentValue])];
      return previousValue;
    }, {});
  };

  const showShareReport = (patient: PatientWithReportInfo) => {
    setErrorMessage({ display: false });
    return sfetch(patient.links.reportSharingInfoLink, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
      },
    }).then((res) => {
      if (res?.ok) return res.json();
      else throw res;
    }).then((reportInfo: SharingReportInfo) => {
      setSharingReportInfo(reportInfo)
    }).then(() => {
      // prevent double scrollbar
      document.body.style.overflow = 'hidden';
      setShowShareReportModal(true);
    }).catch((res) => {
      setErrorMessage({
        display: true,
        content: i18n["Page.Patients.Share.Report.Error"]
      });
      throw res;
    });
  };

  const hideShareReport = () => {
    document.body.style.overflow = 'auto'
    setShowShareReportModal(false)
  }

  const displayShareReport = (patient: PatientWithReportInfo): Boolean => {
    return patient.links.reportSharingInfoLink &&
      [PatientReportStatus.AVAILABLE, PatientReportStatus.SHARED_WITH_PATIENT, PatientReportStatus.VIEWED_BY_PATIENT]
        .includes(patient.latestReport.status)
  }

  const getActionComponent = (patient: PatientWithReportInfo) => {
    const attributes = patient.attributes;
    if (
      (attributes.doctorUid || attributes.practiceUid || attributes.dsoUid) &&
      patient.links.archiveUrl
    ) {
      if (!archived) {
        return (
          <div className="h-flex with-separator no-wrap">
            <span>&nbsp;</span>
            <i
              title={i18n["Page.Reports.Action.Archive"]}
              className="ic_folder icon-button"
              onClick={(e) => {
                e.stopPropagation();
                return setArchive(patient.links.archiveUrl, patient.attributes.doctorUid, true);
              }}
            />
          </div>
        );
      } else {
        return (
          <div className="h-flex with-separator no-wrap">
            <span>&nbsp;</span>
            <i
              title={i18n["Page.Reports.Action.Unarchive"]}
              className="ic_folder icon-button"
              onClick={(e) => {
                e.stopPropagation();
                return setArchive(patient.links.archiveUrl, patient.attributes.doctorUid, false);
              }}
            />
          </div>
        );
      }
    } else return null;
  };

  const onTagAdd = (tagModalInfo: TagModalInfo) => {
    setDisplayAddLabelModal(tagModalInfo.displayModal);
    setCurrentFilter(tagModalInfo.currentFilter);
    setTagSelectedPatient(tagModalInfo.patient);
  };

  columns.forEach((col) => {
    switch (col.path) {
      case "tags":
        const tagsColumn = col as Enum & Column;
        let values = {};
        displayedTags.forEach((tag) => (values[tag.uid] = tag.value));
        tagsColumn.values = values;
        tagsColumn.renderer = (patient: PatientWithReportInfo) => (
          <PatientTagSetter
            patientTags={patient.tags}
            editUrl={patient.links.editTagsUrl}
            availableTags={displayedTags}
            onChange={() => listRef.current.refreshCurrentPage()}
            createTagUrl={createTagUrl}
            patient={patient.patientInformation}
            onTagAdd={onTagAdd}
            displayModalHandler={displayAddLabelModal}
          />
        );
        break;

      case "latestReportStatus":
        const statusColumn = col as Enum & Column;
        if (statusColumn) {
          statusColumn.values = fillI18nColumnValues(
            PatientReportStatus,
            ReportStatusUtils.getStatusTypeI18nKey
          );
          statusColumn.renderer = (patient: PatientWithReportInfo) =>
            ReportStatusUtils.getStatusComponent(patient.latestReport);
        }
        break;

      case "latestReportType":
        const typeColumn = col as Enum & Column;
        if (typeColumn) {
          typeColumn.values = fillI18nColumnValues(FlowType, ReportTypeUtils.getReportTypeI18nKey);
          typeColumn.renderer = (patient: PatientWithReportInfo) => {
            const sharingButton = (<i
              key="share-report-modal"
              title={i18n["Form.SendReport.Title"]}
              className={"ic_mail icon-button" + (displayShareReport(patient) ? "" : " disabled")}
              onClick={(e) => {
                e.stopPropagation();
                return showShareReport(patient);
              }}
            />);
            return ReportTypeUtils.getTypeComponent(patient.latestReport.type, sharingButton);
          }
        }
        break;

      case "patientInformation.insuranceInfoStatus":
        const insuranceInfoColumn = col as Enum & Column;
        if (insuranceInfoColumn) {
          insuranceInfoColumn.values = fillI18nColumnValues(
            InsuranceInfoStatus,
            InsuranceInfoStatusUtils.getStatusTypeI18nKey
          );
          insuranceInfoColumn.renderer = (patient: PatientWithReportInfo) =>
            InsuranceInfoStatusUtils.renderStatus(patient.patientInformation.insuranceInfoStatus);
        }
        break;

      case "action":
        if (col) {
          col.renderer = getActionComponent.bind(this);
        }
        break;

      case "attributes.doctorName":
        if (col) {
          col.renderer = (patient: PatientWithReportInfo) => AssignedToCell({ patient });
        }
        break;

      case "latestReport.clinicalScore":
        const clinicalScoreColumn = col as Enum & Column;
        if (clinicalScoreColumn) {
          clinicalScoreColumn.values = fillI18nColumnValues(
            ReportComplexity,
            ReportComplexityUtils.getReportComplexityI18nKey
          );
          clinicalScoreColumn.renderer = (patient: PatientWithReportInfo) =>
            ReportComplexityUtils.getRenderForLeadList(
              patient.latestReport.complexity,
              patient.latestReport.type
            );
        }
        break;

      default:
        break;
    }
  });

  const setArchive = (url: string, doctorUid: EntitySettingsId, archive: boolean) => {
    return sfetch(url, {
      method: "PUT",
      body: JSON.stringify({
        doctorUid: doctorUid,
        archive: archive,
      }),
      headers: {
        "Content-type": "application/json",
      },
    }).then(() => {
      listRef.current.refreshCurrentPage();
    });
  };

  const additionalFilterQueries = [
    new FilterQuery("PATIENT_IS_ARCHIVED", Operator.EQUAL, archived.toString()),
  ];

  const exportPatients = () => {
    window.open(
      `${exportUrl}${filtersQuery}${filtersQuery.length > 0 ? "&" : "?"}${additionalFilterQueries
        .map((it) => `q=${encodeURIComponent(it.buildQueryString())}`)
        .join("&")}`,
      "_blank"
    );
  };

  const exportButton = exportAuthorized && (
    <button
      className="button-primary"
      onClick={() => {
        if (pageElementsCount <= exportLimit) {
          exportPatients();
        } else {
          setShowExportWarning(true);
        }
      }}
    >
      <i className="ic_download" />
      <span>{i18n["Page.Patients.Export"]}</span>
    </button>
  );

  const closeModal = (state: string) => {
    setErrorMessage({ display: false }); // reset success message animation
    setDisplayAddLabelModal(false);
    if (state === TagModalStates.ERROR) {
      setErrorMessage({
        display: true,
        content: i18n["Page.Settings.Tags.Modal.Add.ErrorMessage"],
      });
    }
  };

  const onTagAdded = (tag: Tag, patientInformation: PatientInformation) => {
    setAddTagToPatientSuccessMessage(false);
    setErrorMessage({ display: false });
    const url = addTagToPatientUrl.replace("uid", patientInformation.contactInfoUid);
    return sfetch(url, {
      method: "PUT",
      body: JSON.stringify({
        tagUid: tag.uid,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(() => {
        setAddTagToPatientSuccessMessage(true);
        setDisplayedTags([...displayedTags, tag]);
        listRef.current.refreshCurrentPage();
      })
      .catch(() => {
        listRef.current.refreshCurrentPage();
        setErrorMessage({
          display: true,
          content: i18n["Page.Settings.Tags.ErrorMessage.Add.WithPatient"],
        });
      });
  };

  return (
    <>
      <HeaderBar
        title={archived ? i18n["Page.ArchivedPatients.Title"] : i18n["Page.Patients.Title"]}
        action={exportButton}
      />
      {errorMessage?.display && (
        <Danger className={"notification-save-error-message"}>{errorMessage.content}</Danger>
      )}
      <div className="paginated-list-container">
        <PaginatedList
          ref={listRef}
          initialPage={page}
          itemKey={(patient: PatientWithReportInfo) =>
            `${patient.patientInformation.contactInfoUid},${patient.attributes.doctorUid}`
          }
          columns={columns}
          emptyListMessage={i18n["Page.Patients.Empty"]}
          additionalFilterQueries={additionalFilterQueries}
          rowOnClick={(patient: PatientWithReportInfo) => redirectTo(patient.links.patientPageLink)}
          onFiltersQueryChange={setFiltersQuery}
          onElementsCountChange={setPageElementsCount}
          fetchUrl={fetchUrl}
          initialSort={sort}
          classNames="with-shadow"
        />
      </div>
      {displayAddLabelModal && (
        <TagModal
          modalType={ModalType.CREATE}
          closeModal={closeModal}
          fetchUrl={createTagUrl}
          onTagAdded={onTagAdded}
          tagPrefilledValue={currentFilter}
          patient={tagSelectedPatient}
        ></TagModal>
      )}
      {addTagToPatientSuccessMessage && (
        <Success className={"notification-save-success-message"}>
          {i18n["Page.Settings.Tags.SuccessMessage.Add.WithPatient"]}
        </Success>
      )}
      {showExportWarning && (
        <ModalWarning
          action={async () => {
            setShowExportWarning(false);
            exportPatients();
          }}
          cancel={() => setShowExportWarning(false)}
          title={i18n["Page.Patients.Export.Warning.Message"].replace("{}", exportLimit)}
          actionButtonLabel={i18n["Page.Patients.Export"]}
        ></ModalWarning>
      )}
      {sharingReportInfo?.shareWithPatientAction && showShareReportModal && (
        <ShareReportModal
          hide={hideShareReport}
          onEditSharingUpdate={() => ""}
          reportUid={sharingReportInfo.reportUid}
          shareAction={sharingReportInfo.shareWithPatientAction}
          reportType={sharingReportInfo.reportType}
          editSharingAction={sharingReportInfo.editSharingAction}
        />
      )}
    </>
  );
}

PatientList.loadComponent = (elementId: string, props: PatientListProps) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<PatientList {...props} />);
};
